import { LineChart } from "lucide-react";
import {
  ListChecks, UserPlus, BarChart3, MessageSquare, Bell, Search, Activity, TrendingUp, Mail, Megaphone,
  Handshake, Target, Printer, Gift, AlertTriangle, Pin,
} from "lucide-react";
import { ACTIVITY_TYPES, PRINT_ORDER_STATUSES } from "@/lib/constants";

export const WIDTHS = [3, 4, 6, 8, 9, 12];
export const WIDTH_LABEL = { 3: "25%", 4: "33%", 6: "50%", 8: "66%", 9: "75%", 12: "100%" };
export const HEIGHTS = { s: 280, m: 420, l: 600, xl: 800 };
export const HEIGHT_LABEL = { s: "Small", m: "Medium", l: "Large", xl: "X-Large" };

const opt = (...pairs) => pairs.map((p) => (Array.isArray(p) ? p : [p, p]));
const SCOPE = { key: "scope", label: "Show", type: "select", options: opt(["mine", "Mine"], ["team", "Everyone"]) };
const LIMIT = { key: "limit", label: "Max items", type: "number" };

export const TASK_BUCKETS = {
  due_by_today: "Today & overdue", overdue: "Overdue", today: "Today", this_week: "Rest of this week",
  next_7: "Next 7 days", upcoming: "Upcoming", no_date: "No due date", all: "All open",
};

export const STAT_METRICS = {
  tasks_completed_week: { label: "Tasks completed this week", href: "/actions/my" },
  activities_logged_week: { label: "Activities logged this week", href: "/actions/my" },
  open_tasks: { label: "Open tasks", href: "/todo?status=open&rel=assigned", teamHref: "/todo?status=open&scope=team" },
  due_today: { label: "Due today", href: "/todo?status=open&due=due_today&rel=assigned", teamHref: "/todo?status=open&due=due_today&scope=team" },
  overdue: { label: "Overdue tasks", href: "/todo?status=open&due=overdue&rel=assigned", teamHref: "/todo?status=open&due=overdue&scope=team" },
  open_opportunities: { label: "Open opportunities", href: "/opportunities" },
  pipeline_value: { label: "Pipeline value", href: "/opportunities", money: true },
  active_collabs: { label: "Active collaborations", href: "/collaborations" },
  unread_chats: { label: "Unread chat messages", href: "/chat" },
  unread_notifications: { label: "Unread notifications", href: "/notifications" },
};

export const BLOCKS = {
  todo: {
    label: "To Do", icon: ListChecks, href: "/todo", addHref: "/todo?new=1",
    fields: [
      { key: "source", label: "Tasks", type: "select", options: opt(["mine", "My Tasks"], ["linked", "Linked Tasks"], ["both", "Both"]) },
      { key: "columns", label: "Columns (in order)", type: "multi", options: Object.entries(TASK_BUCKETS) },
      { key: "sort", label: "Sort by", type: "select", options: opt(["default", "Default"], ["priority", "Priority"], ["due", "Due date"]) },
      { key: "limit", label: "Max per column", type: "number" },
    ],
  },
  stat: {
    label: "Report", icon: BarChart3, autoHeight: true,
    fields: [{ key: "metric", label: "Metric", type: "select", options: Object.entries(STAT_METRICS).map(([k, v]) => [k, v.label]) }, SCOPE],
  },
  chats: {
    label: "Chats", icon: MessageSquare, href: "/chat",
    fields: [{ key: "filter", label: "Show", type: "select", options: opt(["all", "All"], ["unread", "Unread"], ["groups", "Groups"]) }, LIMIT],
  },
  notifications: {
    label: "Notifications", icon: Bell, href: "/notifications",
    fields: [{ key: "unreadOnly", label: "Unread only", type: "toggle" }, LIMIT],
  },
  quick_search: {
    label: "Quick Search", icon: Search, autoHeight: true,
    fields: [{ key: "entities", label: "Search in", type: "multi", options: opt(["properties", "Properties"], ["contacts", "Contacts"], ["organisations", "Organisations"], ["opportunities", "Opportunities"]) }],
  },
  activities: {
    label: "Recent Activities", icon: Activity, href: "/actions/my",
    fields: [SCOPE, { key: "types", label: "Types (none = all)", type: "multi", options: opt(...ACTIVITY_TYPES) }, LIMIT],
  },
  opportunities: {
    label: "Opportunities", icon: TrendingUp, href: "/opportunities",
    fields: [{ key: "view", label: "View", type: "select", options: opt(["pipeline", "Pipeline"], ["list", "List"]) }, SCOPE],
  },
  campaigns: {
    label: "Campaigns", icon: Mail, href: (s) => (s.kind === "agent" ? "/campaigns/agent" : "/campaigns/direct"),
    fields: [
      { key: "kind", label: "Campaigns", type: "select", options: opt(["edm", "Direct"], ["agent", "Agent"], ["all", "All"]) },
      { key: "view", label: "View", type: "select", options: opt(["calendar", "Calendar"], ["list", "List"]) },
      SCOPE,
      { key: "hideDone", label: "Hide completed & cancelled", type: "toggle" },
    ],
  },
  collaborations: {
    label: "Collaborations", icon: Handshake, href: "/collaborations",
    fields: [
      { key: "view", label: "View", type: "select", options: opt(["calendar", "Calendar"], ["board", "Board"], ["list", "List"]) },
      { key: "collabType", label: "Type", type: "select", options: opt(["", "All"], "Influencer", "Giveaway", ["Ongoing", "Ongoing"]) },
      SCOPE,
      { key: "hideDone", label: "Hide closed", type: "toggle" },
    ],
  },
  kpis: { label: "My KPIs", icon: Target, href: "/kpis", fields: [] },
  pace: { label: "Property Pace", icon: LineChart, href: "/stats?tab=pace", fields: [] },
  print_orders: {
    label: "Print Orders", icon: Printer, href: "/print-orders",
    fields: [{ key: "statuses", label: "Statuses (none = all)", type: "multi", options: opt(...PRINT_ORDER_STATUSES) }, LIMIT],
  },
  vouchers: {
    label: "Prize Vouchers", icon: Gift, href: "/prize-vouchers",
    fields: [{ key: "mode", label: "Show", type: "select", options: opt(["recent", "Recently issued"], ["expiring", "Expiring in 30 days"]) }, LIMIT],
  },
  attention: { label: "Needs Attention", icon: AlertTriangle, href: "/organisations", fields: [] },
  noticeboard: { label: "Noticeboard", icon: Pin, href: "/noticeboard", fields: [{ key: "limit", label: "Latest notices (plus pinned)", type: "number" }] },
};

export const PRESETS = [
  { type: "todo", label: "My Tasks", desc: "Your tasks in columns", w: 8, h: "l", settings: { source: "mine", columns: ["due_by_today", "upcoming"], sort: "default", limit: 8 } },
  { type: "todo", label: "Today & This Week", desc: "Two columns sorted by priority", w: 6, h: "l", settings: { source: "mine", columns: ["due_by_today", "this_week"], sort: "priority", limit: 10 } },
  { type: "todo", label: "Linked Tasks", desc: "Tasks you're linked to or mentioned in", icon: UserPlus, w: 4, h: "l", settings: { source: "linked", columns: ["all"], sort: "default", limit: 8 } },
  { type: "stat", label: "Tasks Completed This Week", desc: "Report tile", w: 3, h: "s", settings: { metric: "tasks_completed_week", scope: "mine" } },
  { type: "stat", label: "Report Tile", desc: "Any key number: open tasks, pipeline value…", w: 3, h: "s", settings: { metric: "open_tasks", scope: "mine" } },
  { type: "chats", label: "Chats", desc: "Recent conversations", w: 4, h: "m", settings: { filter: "all", limit: 8 } },
  { type: "notifications", label: "Notifications", desc: "Latest alerts", w: 4, h: "m", settings: { unreadOnly: false, limit: 10 } },
  { type: "quick_search", label: "Quick Search", desc: "Properties, contacts, organisations", w: 3, h: "s", settings: { entities: ["properties", "contacts", "organisations"] } },
  { type: "activities", label: "Recent Activities", desc: "Completed calls, meetings, tasks", w: 6, h: "m", settings: { scope: "mine", types: [], limit: 10 } },
  { type: "opportunities", label: "Opportunities Pipeline", desc: "Deals by stage", w: 12, h: "l", settings: { view: "pipeline", scope: "team" } },
  { type: "campaigns", label: "Direct Campaigns", desc: "Direct campaign calendar", icon: Mail, w: 12, h: "l", settings: { kind: "edm", view: "calendar", scope: "team", hideDone: false } },
  { type: "campaigns", label: "Agent Campaigns", desc: "Agent campaign list", icon: Megaphone, w: 6, h: "m", settings: { kind: "agent", view: "list", scope: "team", hideDone: true } },
  { type: "collaborations", label: "Collaborations", desc: "Collabs calendar", w: 12, h: "l", settings: { view: "calendar", collabType: "", scope: "team", hideDone: false } },
  { type: "kpis", label: "My KPIs", desc: "Progress against targets", w: 12, h: "m", settings: {} },
  { type: "pace", label: "Property Pace", desc: "How every property is pacing vs last year", w: 6, h: "l", settings: {}, page: "reports", path: "/stats" },
  { type: "print_orders", label: "Print Orders", desc: "Orders in progress", w: 6, h: "m", settings: { statuses: ["Ordered", "On Hold", "In Production", "Dispatched"], limit: 8 } },
  { type: "vouchers", label: "Prize Vouchers", desc: "Recent or expiring vouchers", w: 6, h: "m", settings: { mode: "expiring", limit: 8 } },
  { type: "attention", label: "Needs Attention", desc: "Relationships at risk", w: 6, h: "m", settings: {} },
  { type: "noticeboard", label: "Noticeboard", desc: "Pinned and latest company notices", w: 12, h: "m", settings: { limit: 5 } },
];

const uid = () => Math.random().toString(36).slice(2, 10);
export const blockFromPreset = (p) => ({ id: uid(), type: p.type, title: p.label, w: p.w, h: p.h, settings: { ...p.settings } });

const preset = (label) => PRESETS.find((p) => p.label === label);
export const noticeboardBlock = () => blockFromPreset(preset("Noticeboard"));
export const defaultLayout = () => [
  noticeboardBlock(),
  { ...blockFromPreset(preset("Report Tile")), title: "Due Today", settings: { metric: "due_today", scope: "mine" } },
  { ...blockFromPreset(preset("Report Tile")), title: "Overdue", settings: { metric: "overdue", scope: "mine" } },
  blockFromPreset(preset("Tasks Completed This Week")),
  { ...blockFromPreset(preset("Report Tile")), title: "Unread Chats", settings: { metric: "unread_chats", scope: "mine" } },
  blockFromPreset(preset("My Tasks")),
  blockFromPreset(preset("Linked Tasks")),
  { ...blockFromPreset(preset("Agent Campaigns")), title: "My Campaigns", w: 12, settings: { kind: "all", view: "list", scope: "mine", hideDone: false } },
];
